import React, { useState, useEffect } from "react";
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import RadioButton from "../../../components/RadioButton";
import Upload from "../../../components/Upload";
import Dialog from "../../../components/Dialog";
import { showStorageError } from "../../../utils/confirm";
import {
  createCourse as apiCreateCourse,
  updateCourse as apiUpdateCourse,
  apiUrl,
} from "../../../api";
import Swal from "sweetalert2";
import {
  VALID_LINK,
  ALPHA_NUMERIC_SPACE,
  testRegex,
  NUMBERS_ONLY,
} from "../../../config/regex";

const emptyForm = () => ({
  title: "",
  description: "",
  basicVideoUrl: "",
  basicVideoFileName: "",
  duration: "",
  price: "",
  originalPrice: "",
  badges: "",
  imageUrl: "",
  imageFileName: "",
  rating: "",
  studentCount: "",
  courseType: "indian",
  underGrowthPlan: false,
});

const AddCourse = ({ open, onClose, onSuccess, editCourse }) => {
  const [form, setForm] = useState(emptyForm());
  const [initialForm, setInitialForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const isInvalid = React.useMemo(() => {
    const hasErrors =
      Object.keys(errors).filter((k) => k !== "submit").length > 0;
    const hasEmptyFields =
      !String(form.title || "").trim() ||
      !String(form.description || "").trim() ||
      !String(form.basicVideoUrl || "").trim() ||
      !String(form.imageUrl || "").trim() ||
      !String(form.duration || "").trim() ||
      !String(form.rating || "").trim() ||
      !String(form.studentCount || "").trim() ||
      !String(form.price || "").trim() ||
      !String(form.originalPrice || "").trim() ||
      !String(form.badges || "").trim();
    return hasErrors || hasEmptyFields;
  }, [errors, form]);

  const isChanged = React.useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  const isEdit = Boolean(editCourse?.id);

  useEffect(() => {
    if (open && editCourse) {
      const editData = {
        title: editCourse.title || "",
        description: editCourse.description || "",
        basicVideoUrl: editCourse.basicVideoUrl || "",
        basicVideoFileName: editCourse.basicVideoFileName || "",
        duration: editCourse.duration || "",
        price: String(editCourse.price ?? ""),
        originalPrice: String(editCourse.originalPrice ?? ""),
        badges: Array.isArray(editCourse.badges)
          ? editCourse.badges.join(", ")
          : editCourse.badges || "",
        imageUrl: editCourse.imageUrl || "",
        imageFileName: editCourse.imageFileName || "",
        rating: String(editCourse.rating ?? ""),
        studentCount: editCourse.studentCount || "",
        courseType: editCourse.courseType === "crypto" ? "crypto" : "indian",
        underGrowthPlan: Boolean(editCourse.underGrowthPlan),
      };
      setForm(editData);
      setInitialForm(editData);
    } else if (open && !editCourse) {
      const newData = emptyForm();
      setForm(newData);
      setInitialForm(newData);
    }
    if (open) {
      setErrors({});
      setTouched({});
    }
  }, [open, editCourse]);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validate = () => {
    const e = {};
    if (!String(form.title || "").trim()) {
      e.title = "Course title is required";
    } else if (!testRegex(ALPHA_NUMERIC_SPACE, form.title)) {
      e.title = "Title contains invalid characters";
    }

    if (!String(form.description || "").trim())
      e.description = "Description is required";

    if (!String(form.basicVideoUrl || "").trim()) {
      e.basicVideoUrl = "Intro video is required";
    } else if (!testRegex(VALID_LINK, form.basicVideoUrl)) {
      e.basicVideoUrl = "Enter a valid video URL";
    }

    if (!String(form.imageUrl || "").trim()) {
      e.imageUrl = "Course image is required";
    } else if (!testRegex(VALID_LINK, form.imageUrl)) {
      e.imageUrl = "Enter a valid image URL";
    }

    if (!String(form.duration || "").trim())
      e.duration = "Duration is required";

    if (!String(form.rating || "").trim()) {
      e.rating = "Rating is required";
    } else {
      const r = Number(form.rating);
      if (isNaN(r) || r < 0 || r > 5) e.rating = "Enter a valid rating (0-5)";
    }

    if (!String(form.price || "").trim()) {
      e.price = "Special Price is required";
    } else if (!testRegex(NUMBERS_ONLY, String(form.price))) {
      e.price = "Enter a valid price";
    }

    if (!String(form.originalPrice || "").trim()) {
      e.originalPrice = "Original price is required";
    } else if (!testRegex(NUMBERS_ONLY, String(form.originalPrice))) {
      e.originalPrice = "Enter a valid original price";
    }

    if (!String(form.badges || "").trim()) {
      e.badges = "Badges are required";
    }

    if (!e.price && !e.originalPrice) {
      if (Number(form.price) >= Number(form.originalPrice)) {
        e.price = "Special price must be less than original price";
      }
    }

    setErrors((prev) => ({
      ...e,
      ...(prev.submit ? { submit: prev.submit } : {}),
    }));
    return Object.keys(e).length === 0;
  };

  React.useEffect(() => {
    validate();
  }, [
    form.title,
    form.description,
    form.price,
    form.originalPrice,
    form.rating,
    form.basicVideoUrl,
    form.imageUrl,
    form.badges,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      title: form.title.trim(),
      description: form.description || "",
      basicVideoUrl: (form.basicVideoUrl || "").trim() || "",
      basicVideoFileName: form.basicVideoFileName || "",
      duration: form.duration || "",
      price: form.price ? Number(form.price) : 0,
      originalPrice: form.originalPrice ? Number(form.originalPrice) : 0,
      badges: form.badges
        ? form.badges
            .split(",")
            .map((b) => b.trim())
            .filter(Boolean)
        : [],
      imageUrl: (form.imageUrl || "").trim() || "",
      imageFileName: form.imageFileName || "",
      rating: form.rating ? Number(form.rating) : 0,
      studentCount: form.studentCount || "",
      courseType: form.courseType === "crypto" ? "crypto" : "indian",
      underGrowthPlan: Boolean(form.underGrowthPlan),
    };
    try {
      setSaving(true);
      if (isEdit) {
        await apiUpdateCourse(editCourse.id, payload);
      } else {
        await apiCreateCourse(payload);
      }
      setForm(emptyForm());
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Course ${isEdit ? "updated" : "created"} successfully`,
        confirmButtonColor: "#38915c",
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err.name === "QuotaExceededError") {
        showStorageError();
      } else {
        const msg =
          err.response?.data?.message || err.message || "Request failed";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: msg,
          confirmButtonColor: "#38915c",
        });
        setErrors({ submit: msg });
      }
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        type="submit"
        form="add-course-form"
        disabled={saving || isInvalid || !isChanged}
      >
        {saving ? "Saving..." : isEdit ? "Update Course" : "Add Course"}
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Course" : "Add Course"}
      size="xl"
      footer={footer}
    >
      <form
        id="add-course-form"
        onSubmit={handleSubmit}
        noValidate
        className="space-y-4"
      >
        <TextInput
          name="title"
          label="Course name"
          placeholder="Course title"
          value={form.title}
          onChange={(e) => {
            setForm((f) => ({ ...f, title: e.target.value }));
            setTouched((t) => ({ ...t, title: true }));
          }}
          onBlur={() => handleBlur("title")}
          error={touched.title ? errors.title : ""}
          required
        />
        <Upload
          type="video"
          label="Basic / intro video (1 per course)"
          value={form.basicVideoUrl}
          fileName={form.basicVideoFileName}
          uploadEndpoint={apiUrl("/api/courses/upload-video")}
          onChange={(val, fname) => {
            setForm((f) => ({
              ...f,
              basicVideoUrl: val,
              basicVideoFileName: fname || "",
            }));
            setTouched((t) => ({ ...t, basicVideoUrl: true }));
          }}
          onBlur={() => handleBlur("basicVideoUrl")}
          placeholder="https://... video URL"
          error={touched.basicVideoUrl ? errors.basicVideoUrl : ""}
          required
        />
        <TextInput
          name="description"
          label="Description"
          placeholder="Course description"
          value={form.description}
          onChange={(e) => {
            setForm((f) => ({ ...f, description: e.target.value }));
            setTouched((t) => ({ ...t, description: true }));
          }}
          onBlur={() => handleBlur("description")}
          multiline
          rows={3}
          error={touched.description ? errors.description : ""}
          required
        />
        <Upload
          type="image"
          label="Course image"
          value={form.imageUrl}
          fileName={form.imageFileName}
          uploadEndpoint={apiUrl("/api/courses/upload-image")}
          onChange={(val, fname) => {
            setForm((f) => ({
              ...f,
              imageUrl: val,
              imageFileName: fname || "",
            }));
            setTouched((t) => ({ ...t, imageUrl: true }));
          }}
          onBlur={() => handleBlur("imageUrl")}
          placeholder="https://... image URL"
          error={touched.imageUrl ? errors.imageUrl : ""}
          required
        />
        <TextInput
          name="badges"
          label="Badges (comma-separated)"
          placeholder="e.g. Premium Course, Bestseller"
          value={form.badges}
          onChange={(e) => {
            setForm((f) => ({ ...f, badges: e.target.value }));
            setTouched((t) => ({ ...t, badges: true }));
          }}
          onBlur={() => handleBlur("badges")}
          error={touched.badges ? errors.badges : ""}
          required
        />

        <RadioButton
          label="Course type"
          name="courseType"
          value={form.courseType}
          onChange={(e) =>
            setForm((f) => ({ ...f, courseType: e.target.value }))
          }
          options={[
            { label: "Indian", value: "indian" },
            { label: "Crypto", value: "crypto" },
          ]}
          row
          required
        />

        <RadioButton
          label="Is this course under Growth subscription plan?"
          name="underGrowthPlan"
          value={String(form.underGrowthPlan)}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              underGrowthPlan: e.target.value === "true",
            }))
          }
          options={[
            { label: "Yes", value: "true" },
            { label: "No", value: "false" },
          ]}
          row
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput
            name="duration"
            label="Duration"
            placeholder="e.g. 6 hrs 30 min or 4 weeks"
            value={form.duration}
            onChange={(e) => {
              setForm((f) => ({ ...f, duration: e.target.value }));
              setTouched((t) => ({ ...t, duration: true }));
            }}
            onBlur={() => handleBlur("duration")}
            error={touched.duration ? errors.duration : ""}
            required
          />
          <TextInput
            name="rating"
            label="Rating (e.g. 4.7)"
            placeholder="4.7"
            value={form.rating}
            onChange={(e) => {
              setForm((f) => ({ ...f, rating: e.target.value }));
              setTouched((t) => ({ ...t, rating: true }));
            }}
            onBlur={() => handleBlur("rating")}
            error={touched.rating ? errors.rating : ""}
            required
          />
          <TextInput
            name="studentCount"
            label="Students (e.g. 6k+)"
            placeholder="6k+ Students"
            value={form.studentCount}
            onChange={(e) => {
              setForm((f) => ({ ...f, studentCount: e.target.value }));
              setTouched((t) => ({ ...t, studentCount: true }));
            }}
            onBlur={() => handleBlur("studentCount")}
            error={touched.studentCount ? errors.studentCount : ""}
            required
          />
          <TextInput
            name="price"
            label="Special price (₹)"
            placeholder="e.g. 4999"
            value={form.price}
            onChange={(e) => {
              setForm((f) => ({ ...f, price: e.target.value }));
              setTouched((t) => ({ ...t, price: true }));
            }}
            onBlur={() => handleBlur("price")}
            error={touched.price ? errors.price : ""}
            required
          />
          <TextInput
            name="originalPrice"
            label="Original price (₹) – shown crossed out"
            placeholder="e.g. 9999"
            value={form.originalPrice}
            onChange={(e) => {
              setForm((f) => ({ ...f, originalPrice: e.target.value }));
              setTouched((t) => ({ ...t, originalPrice: true }));
            }}
            onBlur={() => handleBlur("originalPrice")}
            error={touched.originalPrice ? errors.originalPrice : ""}
            required
          />
        </div>
      </form>
    </Dialog>
  );
};

export default AddCourse;
